"use client";

import { Ico20ArrowRight } from "@/shared/assets/icons/ico_20_arrow_right";
import Link from "next/link";
import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import "swiper/css";

const contentData = [
  {
    image: "/images/home/MPS_01.webp",
    title: "mn_pd_pn_2_title_1",
    descriptions: ["mn_pd_pn_2_context_1_1", "mn_pd_pn_2_context_1_2"],
    activeTag: 0,
  },
  {
    image: "/images/home/MPS_02.webp",
    title: "mn_pd_pn_2_title_2",
    descriptions: ["mn_pd_pn_2_context_2"],
    activeTag: 1,
  },
  {
    image: "/images/home/MPS_03.webp",
    title: "mn_pd_pn_2_title_3",
    descriptions: ["mn_pd_pn_2_context_3"],
    activeTag: 2,
  },
  {
    image: "/images/home/MPS_04.webp",
    title: "mn_pd_pn_2_title_4",
    descriptions: ["mn_pd_pn_2_context_4_1", "mn_pd_pn_2_context_4_2"],
    activeTag: 3,
  },
];

const tags = ["mn_pd_pn_2_title_1", "mn_pd_pn_2_title_2", "mn_pd_pn_2_title_3", "mn_pd_pn_2_title_4"];

const mdsContentData = [
  {
    image: "/images/home/MDS_01.webp",
    title: "mn_pd_pn_3_title_1",
    descriptions: ["mn_pd_pn_3_context_1_1", "mn_pd_pn_3_context_1_2"],
    activeTag: 0,
  },
  {
    image: "/images/home/MDS_02.webp",
    title: "mn_pd_pn_3_title_2",
    descriptions: ["mn_pd_pn_3_context_2_1", "mn_pd_pn_3_context_2_2"],
    activeTag: 1,
  },
  {
    image: "/images/home/MDS_03.webp",
    title: "mn_pd_pn_3_title_3",
    descriptions: ["mn_pd_pn_3_context_3_1", "mn_pd_pn_3_context_3_2"],
    activeTag: 2,
  },
  {
    image: "/images/home/MDS_04.webp",
    title: "mn_pd_pn_3_title_4",
    descriptions: [
      "mn_pd_pn_3_context_4_1",
      "mn_pd_pn_3_context_4_2",
      "mn_pd_pn_3_context_4_3",
      "mn_pd_pn_3_context_4_4",
    ],
    activeTag: 3,
  },
];

const mdsTags = ["mn_pd_pn_3_title_1", "mn_pd_pn_3_title_2", "mn_pd_pn_3_title_3", "mn_pd_pn_3_title_4"];

export function SoftwarePC() {
  const t = useTranslations();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mdsCurrentIndex, setMdsCurrentIndex] = useState(0);

  const mpsSwiper = useRef<SwiperType | null>(null);
  const mdsSwiper = useRef<SwiperType | null>(null);

  const handleTagClick = (index: number, isMedia: boolean = false) => {
    if (isMedia) {
      mdsSwiper.current?.slideTo(index);
    } else {
      mpsSwiper.current?.slideTo(index);
    }
  };

  const handleSlideChange = (swiper: SwiperType, isMedia: boolean = false) => {
    if (isMedia) {
      setMdsCurrentIndex(swiper.realIndex);
    } else {
      setCurrentIndex(swiper.realIndex);
    }
  };

  return (
    <>
      <div className="m-auto flex h-[693px] w-full max-w-[1280px] flex-col items-center justify-center bg-white">
        <div className="mb-10 flex w-full items-end justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="heading-1 text-gray-900">MPS</span>
            <span className="heading-4-regular text-gray-500">{t("mn_pd_pn_2_1")}</span>
          </div>

          <Link href="/solution?tab=mps" className="body-md-regular flex items-center gap-[6px] text-gray-700">
            {t("mn_pd_pn_2_btn")} <Ico20ArrowRight className="size-5" />
          </Link>
        </div>

        <div className="flex w-full max-w-[1280px] gap-5">
          <div className="rounded-24 relative max-h-[548px] w-full max-w-[847px] overflow-hidden">
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => (mpsSwiper.current = swiper)}
              onSlideChange={(swiper) => handleSlideChange(swiper, false)}
              className="h-full w-full"
              allowTouchMove={true}
              spaceBetween={0}
              slidesPerView={1}
              loop={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
            >
              {contentData.map((content, index) => (
                <SwiperSlide key={index} className="opacity-100">
                  <div className="rounded-24 h-full w-full bg-black">
                    <img
                      src={content.image}
                      alt={`MPS_${index + 1}`}
                      width={847}
                      height={548}
                      className="h-auto w-full"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className="rounded-24 flex w-full max-w-[413px] flex-col justify-between border border-gray-100 bg-white px-10 pt-12.5 pb-15 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col gap-3">
              <span className="heading-4-bold text-gray-900">{t(contentData[currentIndex].title)}</span>
              <div className="flex flex-col gap-1">
                {contentData[currentIndex].descriptions.map((desc, index) => (
                  <div key={index} className="body-lg-regular flex items-start gap-[6px] text-[16px] text-gray-500">
                    <span>·</span>
                    <span>{t(desc)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {tags.map((tag, index) => (
                <div
                  key={index}
                  onClick={() => handleTagClick(index, false)}
                  className={`body-md-medium w-fit cursor-pointer rounded-full px-4 py-[10px] text-[14px] transition-colors duration-300 ${
                    index === currentIndex ? "bg-primary-100 text-primary-500 body-md-bold" : "bg-gray-50 text-gray-400"
                  }`}
                >
                  {t(tag)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MDS 섹션 */}
      <div className="m-auto mt-20 flex h-[693px] w-full max-w-[1280px] flex-col items-center justify-center bg-white">
        <div className="mb-10 flex w-full items-end justify-between">
          <div className="flex flex-col items-start">
            <span className="heading-1 text-gray-900">MDS</span>
            <span className="heading-4-regular text-gray-500">{t("mn_pd_pn_3_1")}</span>
          </div>

          <Link href="/solution?tab=mds" className="body-md-regular flex items-center gap-[6px] text-gray-700">
            {t("mn_pd_pn_3_btn")} <Ico20ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="flex w-full max-w-[1280px] gap-5">
          {/* 텍스트 카드 - 왼쪽 */}
          <div className="rounded-24 flex w-full max-w-[413px] flex-col justify-between border border-gray-100 bg-white px-10 pt-12.5 pb-15 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col gap-3">
              <span className="heading-4-bold text-gray-900">{t(mdsContentData[mdsCurrentIndex].title)}</span>
              <div className="flex flex-col gap-1">
                {mdsContentData[mdsCurrentIndex].descriptions.map((desc, index) => (
                  <div key={index} className="body-lg-regular flex items-start gap-[6px] text-[16px] text-gray-500">
                    <span>·</span>
                    <span>{t(desc)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {mdsTags.map((tag, index) => (
                <div
                  key={index}
                  onClick={() => handleTagClick(index, true)}
                  className={`body-md-medium w-fit cursor-pointer rounded-full px-4 py-[10px] text-[14px] transition-colors duration-300 ${
                    index === mdsCurrentIndex
                      ? "bg-primary-100 text-primary-500 body-md-bold"
                      : "bg-gray-50 text-gray-400"
                  }`}
                >
                  {t(tag)}
                </div>
              ))}
            </div>
          </div>

          {/* 이미지 카드 - 오른쪽 */}
          <div className="rounded-24 relative min-h-[548px] w-full max-w-[847px] overflow-hidden">
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => (mdsSwiper.current = swiper)}
              onSlideChange={(swiper) => handleSlideChange(swiper, true)}
              className="h-full w-full"
              allowTouchMove={true}
              spaceBetween={0}
              slidesPerView={1}
              loop={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
            >
              {mdsContentData.map((content, index) => (
                <SwiperSlide key={index} className="opacity-100">
                  <div className="rounded-24 h-full w-full bg-black">
                    <img
                      src={content.image}
                      alt={`MDS_${index + 1}`}
                      width={847}
                      height={548}
                      className="h-auto w-full"
                      style={{
                        maxWidth: "calc(100% - 0px)",
                        height: "auto",
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </>
  );
}
